import * as React from "react";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import PostAddIcon from "@mui/icons-material/PostAdd";
import ScienceIcon from "@mui/icons-material/Science";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";
import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { Box, Typography, Card, Grid } from "@mui/material";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";

import Can from "../../Policy/Can";
import useCan from "../../../hooks/useCan";
import { tl } from "../../../components/translate";
import { getUserAccesingRcs } from "../../../slices/userBranchSlice";
import { PolicyAccessKeys } from "../../../interfaces/policy";
import { reportCardStyle, reportsCardContainerStyle } from "../ReportTypesList";

// @ts-expect-error - css
import styles from "../Reports.module.css";

const BranchReports = (): React.ReactNode => {
  const dispatch = useDispatch();
  const canAccessBranchRcs = useCan(PolicyAccessKeys.CmsReport, { superAdminPass: true });

  React.useEffect(() => {
    if (canAccessBranchRcs) {
      dispatch(getUserAccesingRcs());
    }
  }, [canAccessBranchRcs, dispatch]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ ...reportsCardContainerStyle }}>
        {/* due report */}
        <Can policyAccessKey={PolicyAccessKeys.DueReport}>
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => dispatch(push("/reports/branchReports/dueReport"))}
            data-testmation="dueReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.dueReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <AssignmentReturnIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>

        {/* receipt report */}
        <Can policyAccessKey={PolicyAccessKeys.ReceiptReport}>
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => dispatch(push("/reports/branchReports/receiptReport"))}
            data-testmation="receiptReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.receiptReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ReceiptIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>

        {/* stock audit report */}
        <Can policyAccessKey={PolicyAccessKeys.StockAuditReport}>
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => dispatch(push("/reports/branchReports/stockAuditReport"))}
            data-testmation="stockAuditReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    Stock Audit Report
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <VerifiedUserIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>

        {/* lab report */}
        <Can policyAccessKey="lab:listLab">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => dispatch(push("/reports/branchReports/labReport"))}
            data-testmation="branchLabReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    Lab Report
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ScienceIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>

        {/* patient prescription */}
        <Can policyAccessKey="report:patientAssessmentReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => dispatch(push("/reports/branchReports/patientAssessmentReport"))}
            data-testmation="branchPatientAssessmentReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    Patient Prescription Report
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <BubbleChartIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:salesReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => dispatch(push("/reports/branchReports/salesReport"))}
            data-testmation="salesReportCard"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.salesReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ChromeReaderModeIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>

        {/* sales by service report */}
        <Can policyAccessKey={PolicyAccessKeys.SalesByServiceReport}>
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => dispatch(push("/reports/branchReports/salesByServiceReport"))}
            data-testmation="salesByServiceReportCard"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.salesByServiceReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <PostAddIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>

        {/* sales by service provider report */}
        <Can policyAccessKey={PolicyAccessKeys.SalesByServiceReport}>
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => dispatch(push("/reports/branchReports/salesByServiceProviderReport"))}
            data-testmation="salesByServiceProviderReportCard"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.salesByServiceProviderReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ReceiptLongIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
      </Box>
    </Box>
  );
};

export default BranchReports;
